import { Modal } from '@/components/Modal'
import { MessageTemplateModel } from '@/models/message.model'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MdCall, MdClose, MdOutlineSecurity, MdSearch } from 'react-icons/md'
import * as S from './VoiceModals.styles'

type CallPermissionModalProps = {
  isOpen: boolean
  templates: MessageTemplateModel[]
  selectedTemplateId: string
  search: string
  contactName: string
  loading: boolean
  submitting: boolean
  onSearchChange: (search: string) => void
  onSelectTemplate: (templateId: string) => void
  onClose: () => void
  onSubmit: () => void
}

function templateBody(template: MessageTemplateModel) {
  return template.components.find(component => component.type === 'BODY')?.text || ''
}

function searchable(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

export function CallPermissionModal({
  isOpen,
  templates,
  selectedTemplateId,
  search,
  contactName,
  loading,
  submitting,
  onSearchChange,
  onSelectTemplate,
  onClose,
  onSubmit
}: CallPermissionModalProps) {
  const { t } = useTranslation()
  const visibleTemplates = useMemo(() => {
    const query = searchable(search.trim())
    if (!query) return templates
    return templates.filter(template => searchable(`${template.name} ${templateBody(template)}`).includes(query))
  }, [search, templates])
  const selectedTemplate = templates.find(template => template.id === selectedTemplateId)
  const canSubmit = Boolean(selectedTemplate) && !submitting

  return (
    <Modal isOpen={isOpen} closeModal={onClose} hideHeaderCloseButton>
      <S.ModalFrame role="dialog" aria-modal="true" aria-labelledby="voice-permission-title">
        <S.ModalHeader>
          <S.HeaderIcon>
            <MdOutlineSecurity size={22} />
          </S.HeaderIcon>
          <S.HeaderCopy>
            <h2 id="voice-permission-title">{t('translation:voice.permission.title')}</h2>
            <p>
              {t(
                'translation:voice.permission.templateDescription',
                'Escolha o modelo de convite que será enviado para {{name}}.',
                { name: contactName }
              )}
            </p>
          </S.HeaderCopy>
        </S.ModalHeader>

        <S.ModalBody>
          <S.TemplateSearch>
            <MdSearch size={20} />
            <input
              autoFocus
              value={search}
              onChange={event => onSearchChange(event.target.value)}
              placeholder={t('translation:voice.permission.searchTemplates')}
              aria-label={t('translation:voice.permission.searchTemplates')}
            />
          </S.TemplateSearch>

          <S.TemplateList aria-label={t('translation:voice.permission.availableTemplates')}>
            {loading ? (
              <S.TemplateEmpty>{t('translation:voice.permission.loadingTemplates')}</S.TemplateEmpty>
            ) : visibleTemplates.length ? (
              visibleTemplates.map(template => {
                const selected = template.id === selectedTemplateId
                return (
                  <S.TemplateOption key={template.id} $selected={selected}>
                    <input
                      type="radio"
                      name="call-permission-template"
                      value={template.id}
                      checked={selected}
                      onChange={() => onSelectTemplate(template.id)}
                    />
                    <S.TemplateCopy>
                      <S.TemplateHeading>
                        <strong>{template.name}</strong>
                        <S.CategoryBadge $category={template.category}>{template.category}</S.CategoryBadge>
                      </S.TemplateHeading>
                      <span>{templateBody(template)}</span>
                    </S.TemplateCopy>
                  </S.TemplateOption>
                )
              })
            ) : (
              <S.TemplateEmpty>{t('translation:voice.permission.noCallTemplates')}</S.TemplateEmpty>
            )}
          </S.TemplateList>

          {selectedTemplate && (
            <S.SelectedTemplate>
              <small>{t('translation:voice.permission.selectedTemplate')}</small>
              <strong>{selectedTemplate.name}</strong>
            </S.SelectedTemplate>
          )}
        </S.ModalBody>

        <S.ModalFooter>
          <S.Button type="button" onClick={onClose} disabled={submitting}>
            <MdClose size={18} /> {t('translation:voice.permission.cancel')}
          </S.Button>
          <S.Button type="button" $primary onClick={onSubmit} disabled={!canSubmit}>
            <MdCall size={18} />
            {submitting ? t('translation:voice.permission.sending') : t('translation:voice.permission.sendInvite')}
          </S.Button>
        </S.ModalFooter>
      </S.ModalFrame>
    </Modal>
  )
}
